<script setup>
  import { ref, computed, inject, watchEffect, useTemplateRef, onMounted, watch } from 'vue';
  import { useUserStore } from "@/stores/UserStore";
  import { useSpotStore } from "@/stores/SpotStore";
  import { useUserUpgrades } from '@/composables/useUserUpgrades';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useUpdateKey } from '@/composables/useUpdateKey';
  import { useWindGraphModelSelection } from '@/composables/useWindGraphModelSelection';
  import DataContainer from "@/components/DataContainer.vue";
  import ModelSelector from "@/components/ModelSelector.vue";
  import WindGraphBasic from '@/components/WindGraphBasic.vue';
  import WindGraphDetailed from '@/components/WindGraphDetailed.vue';
  import WindGraphMultiStationList from '@/components/WindGraphMultiStationList.vue';
  import WindGraphsToolbar from '@/components/WindGraphsToolbar.vue';
  import WindGraphsUpgradeMessaging from '@/components/WindGraphsUpgradeMessaging.vue';
  import CurrentConditionsGaugeContainer from '@/components/CurrentConditionsGaugeContainer.vue';
  import CurrentConditionsDataContainer from '@/components/CurrentConditionsDataContainer.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   

  const emit = defineEmits(['containerSettings:change', 'modelsSelected:change']);
  const content = defineModel('content');

  const windGraphFooterContainer = useTemplateRef('wind-graph-footer-container');

  const props = defineProps({
    containerSettings: {
      type: Object,
      required: false
    },
    showCurrentConditions: {
      type: Boolean,
      default: false
    },
    controlsTarget: {
      type: HTMLElement,
      required: false
    },
    sidebarTarget: {
      type: HTMLElement,
      required: false
    },
    showLastUpdateTimestamp: {
      type: Boolean,
      default: false
    },
    windGraphStartingView: {
      type: String,
      default: 'basic'
    },
    windGraphType: {
      type: String,
      required: false
    },
    windGraphDataField: {
      type: String,
      required: false
    },
    isFavorite: {
      type: Boolean,
      default: false
    },
    upgrade: {
      required: false
    },    
  })

  defineExpose({ getSettings, setNCWind });

  const spotId = props.isFavorite && props.containerSettings && Object.prototype.hasOwnProperty.call(props.containerSettings, 'spotId')
    ? parseInt(props.containerSettings.spotId)
    : null;

  const uniqueStoreKey = props.isFavorite && props.containerSettings ? Date.now() : null;
  
  const userStore = useUserStore();
  const spotStore = useSpotStore(uniqueStoreKey);

  if (props.isFavorite && spotId) {
    await spotStore.getSpotDetailById(spotId, { spot_types: "1,100,101" });
  }

  const isDesktop = useMediaQuery('(min-width: 840px)');
  const updateKey = useUpdateKey();

  let isNCWind = inject('isNCWind', spotStore.isNCWind);

  function computeInitialGraphType() {
    if ($WEB_VIEW || !isDesktop.value) return 'basic';
    if (props.windGraphType) return props.windGraphType;
    if (props.containerSettings && props.containerSettings.type) {
      return props.containerSettings.type === 'Advanced' ? 'detailed' : 'basic';
    }
    return props.windGraphStartingView;
  }

  const graphType = ref(computeInitialGraphType());
  const basicGraph = ref(null);
  const detailedGraph = ref(null);

  // Model selection (storage, defaults, Pro Forecast, EventBus) lives here.
  // Spot identity comes from the store (the parent's, not the selector's).
  const {
    selectedModelIds,
    unavailableModelIds,
    includeNWSMarineForecast,
    additionalModels: selectorAdditionalModels,
    onModelsLoaded,
    onModelsSelected,
  } = useWindGraphModelSelection({
    spot: () => spotStore.spotApiData,
    spotId: () => spotStore.spotId,
    variant: graphType,
    storeKey: uniqueStoreKey,
    initialSelection: props.isFavorite ? props.containerSettings?.selectedModelIds ?? null : null,
  });

  const zoomLevel = ref(12);
  const dataField = ref('wind');
  const multiStationSpotList = ref(null);
  const multiStationGraphData = ref(null);

  const userUpgradeInfo = computed(() => {
    if (props.upgrade) return props.upgrade;

    const { upgrade } = useUserUpgrades({ storeId: uniqueStoreKey });

    return upgrade.value;
  });
  
  const upgradeAvailable = computed(() => userUpgradeInfo.value[0]);
  // Both graph types use the Vue ModelSelector now.
  const showBasicWindGraph = computed(() => !upgradeAvailable.value && graphType.value === 'basic');
  const showDetailedWindGraph = computed(() => !upgradeAvailable.value && graphType.value === 'detailed');
  const showNCWindToggle = computed(() => userStore.showNCWindToggle && spotStore.isNCWindCapable);
  const showMultiStationDisplay = computed(() => isDesktop.value && graphType.value === 'detailed' && (multiStationSpotList.value?.length > 1 || (spotStore.isMultiStationSpot && multiStationSpotList.value?.length > 0)) );
  const showCurrentConditionsDisplay = computed(() => isDesktop.value && props.showCurrentConditions && !showMultiStationDisplay.value);
  const showSidebar = computed(() => showCurrentConditionsDisplay.value || showMultiStationDisplay.value);

  const sidebarClasses = computed(() => ({
    'jwx-data-container__sidebar': !props.sidebarTarget,
    'jwx-flex': true,
    'jwx-flex-column': true,
  }));

  function setNCWind(value) {
    if (!content.value) return;

    spotStore.updateNCWindDataEnabled(value);
    content.value.useNCWindData(value);  
  }

  function getSettings() {
    if (graphType.value === 'basic' && basicGraph.value) {
      return basicGraph.value.getSettings();
    } else if (graphType.value === 'detailed' && detailedGraph.value) {
      return detailedGraph.value.getSettings();
    }

    return null;
  }

  function getMultiStationSpotList() {
    return spotStore.isMultiStationSpot ? [spotStore.spot] : null;  
  }

  function handleWindGraphTypeChange(windGraphType) {
    // Webviews only ever show the basic graph.
    if ($WEB_VIEW) return;

    graphType.value = windGraphType;

    if (windGraphType === 'detailed' && !multiStationSpotList.value?.length) {
      multiStationSpotList.value = getMultiStationSpotList();
    }
  }

  function handleWindGraphZoomChange(newZoom) {
    zoomLevel.value = newZoom;

    content.value.zoom(newZoom);
  }

  function handleNearbySpotsChange(selectedNearbySpots) {
    multiStationSpotList.value = selectedNearbySpots;
  }
  
  function handleDataFieldChange(field) {
    dataField.value = field;

    window.$EventBus.dispatchEvent('data-field-change', { field });
  }

  function handleNearcastWindChange(value) {
    setNCWind(value);
  }  

  function handleMultiStationListChange(event) {
    multiStationGraphData.value = event;
  }

  function emitSettingsChange() {
    if (!content.value) return;

    emit('containerSettings:change');      
  }

  watch(zoomLevel, () => {
    emitSettingsChange();      
  });

  watchEffect(() => {
    content.value = graphType.value === 'basic' || !graphType.value ? basicGraph.value : detailedGraph.value;

    emitSettingsChange();      
  }, { flush: 'post' });

  watchEffect(() => {
    // Webviews only ever show the basic graph, so don't let a parent-supplied
    // windGraphType (e.g. "detailed") override the basic default.
    if (!props.windGraphType || $WEB_VIEW) return;

    graphType.value = props.windGraphType;
  });

  onMounted(() => {
    if (props.containerSettings && Object.keys(props.containerSettings).length > 0) {
      zoomLevel.value = parseInt(props.containerSettings.zoom);
    };

    if (graphType.value === 'detailed') {
      multiStationSpotList.value = getMultiStationSpotList()
    }

    if (props.windGraphDataField) dataField.value = props.windGraphDataField;
  });

</script>

<template>
  <DataContainer style="width: 100%; height: 100%;">
    <Teleport defer :disabled="!props.controlsTarget" :to="props.controlsTarget">
      <div v-if="!upgradeAvailable" class="jwx-data-container__controls">
        <WindGraphsToolbar
          @graphType:change="handleWindGraphTypeChange"
          @basicGraphZoomLevel:change="handleWindGraphZoomChange"
          @dataField:change="handleDataFieldChange"
          @nearbySpots:change="handleNearbySpotsChange"
          @nearcastWind:change="handleNearcastWindChange"
          :showNCWindToggle="showNCWindToggle"
          :ncWindDataEnabled="spotStore.ncWindDataEnabled"
          :windGraphType="graphType"
          :modelsSelected="selectedModelIds"
          :dataField="dataField"    
          :spotId="spotId" 
          :zoom="zoomLevel"       
        />
        <div class="jw-model-selector-container">
          <ModelSelector
            :id="`wind-graph-model-selector-${spotStore.spotId}`"
            multiSelect
            :includeNWSMarineForecast="includeNWSMarineForecast"
            :spotId="spotStore.spotId"
            :favSpotId="spotStore.spotFavoriteId"
            :lat="spotStore.lat"
            :lon="spotStore.lon"
            :additionalModels="selectorAdditionalModels"
            :unavailableModelIds="unavailableModelIds"
            :analytics="{
              initiator: 'Wind_Graph',
              spotName: spotStore.spotName,
              spotId: spotStore.spotId,
              lat: spotStore.lat,
              lon: spotStore.lon
            }"
            v-model="selectedModelIds"
            @input:change="onModelsSelected"
            @models:loaded="onModelsLoaded"
          />
        </div>
      </div>
    </Teleport>
    <div
      class="jwx-data-container__content"
      :data-has-sidebar="showSidebar"
    >
      <Teleport defer :disabled="!showSidebar || !props.sidebarTarget" :to="props.sidebarTarget">
        <div v-if="showSidebar" :class="sidebarClasses" style="height: 100%; max-width: 15rem;">
          <WindGraphMultiStationList
            v-if="showMultiStationDisplay"
            @multiStationList:change="handleMultiStationListChange"
            :spots="multiStationSpotList"
          />
  
          <CurrentConditionsGaugeContainer
            v-if="showCurrentConditionsDisplay"
            :upgrade="userUpgradeInfo"
            :spotDataObject="spotStore.spotApiData"
            data-condensed="true"
            style="flex: 0"
          />

          <CurrentConditionsDataContainer 
            v-if="showCurrentConditionsDisplay"
            :upgrade="userUpgradeInfo"
            :spotDataObject="spotStore.spotApiData"
            :useButtons="graphType === 'detailed'"
            :showLastUpdateTimestamp="props.showLastUpdateTimestamp"
            data-condensed="true"
            @dataItem:click="handleDataFieldChange"
            style="flex: 1"
          />
        </div>
      </Teleport>
      <WindGraphsUpgradeMessaging
        v-if="upgradeAvailable"
        :upgrade="userUpgradeInfo"
        :showProforecastMessaging="spotStore.proForecastIdIfExists ? true : false"
      />
      <WindGraphBasic
        v-if="showBasicWindGraph"
        v-model:content="basicGraph"
        v-model:selectedModels="selectedModelIds"
        :isNowcast="spotStore.isNowcast || spotStore.isArbitrarySpot"
        :isNCWind="isNCWind"
        :ncWindDataEnabled="spotStore.ncWindDataEnabled"
        :selectedModelIds="selectedModelIds"
        :zoom="zoomLevel"
        :legendTarget="windGraphFooterContainer"
        :spotDataObject="spotStore.spotApiData"
        :useUniqueStoreKey="props.isFavorite && props.containerSettings ? true : false"
        :updateKey="updateKey.key.value"
      />
      <WindGraphDetailed
        v-if="showDetailedWindGraph"
        v-model:content="detailedGraph"
        :isNCWind="isNCWind"
        :ncWindDataEnabled="spotStore.ncWindDataEnabled"
        :selectedModelIds="selectedModelIds"
        :useExternalDataBlock="true"
        :useExternalControls="true"
        :useClassicModelSelector="false"
        :dataField="dataField"
        :multiStationGraphData="multiStationGraphData"
        :spotDataObject="spotStore.spotApiData"
        :updateKey="updateKey.key.value"
      />
    </div>
    <div
      class="jwx-data-container__footer jw-graph-legend-container jw-graph-legend"
      ref="wind-graph-footer-container"
    ></div>
  </DataContainer>
</template>
